import PropTypes from "prop-types";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { calculateInvestorFees, formatCurrency } from "../utils/investorFees";
import { notifyUser } from "../utils/notifyUser";

/**
 * North Capital ACH Payment Form with Plaid Integration
 *
 * Flow:
 * 1. User clicks "Confirm Investment"
 * 2. Create trade via North Capital API
 * 3. Link external account (get Plaid URL)
 * 4. Show Plaid iframe for bank connection
 * 5. On Plaid success, execute fund movement
 */

function NorthCapitalAchForm({
    offeringId,
    amount,
    campaignId,
    acceptanceToken,
    user,
    northCapitalAccountId,
}) {
    const navigate = useNavigate();
    const iframeRef = useRef(null);
    const feeBreakdown = calculateInvestorFees(amount, "ach");

    // Flow states
    const [currentStep, setCurrentStep] = useState("ready");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Trade details
    const [tradeId, setTradeId] = useState(null);
    const [, setTransactionId] = useState(null);

    // Plaid iframe state
    const [plaidUrl, setPlaidUrl] = useState(null);
    const [showIframe, setShowIframe] = useState(false);
    const [plaidConnectionSuccessful, setPlaidConnectionSuccessful] =
        useState(false);

    // Existing bank account detection
    const [hasLinkedBank, setHasLinkedBank] = useState(false);
    const [linkedBankInfo, setLinkedBankInfo] = useState(null);
    const [checkingLinkedBank, setCheckingLinkedBank] = useState(true);
    // Default to 'new', will be updated based on hasLinkedBank
    const [paymentOption, setPaymentOption] = useState("new");

    // Update payment option when linked bank status changes
    useEffect(() => {
        if(hasLinkedBank) {
            // Default to using existing bank if available
            setPaymentOption("existing");
        } else {
            // Default to new bank for first-time users
            setPaymentOption("new");
        }
    }, [hasLinkedBank]);

    // Reusable function to check for linked bank accounts with retry logic
    const checkLinkedBankAccount = async (
        showLoadingState = true,
        retryAttempts = 3,
        delayMs = 2000,
    ) => {
        try {
            if(showLoadingState) {
                setCheckingLinkedBank(true);
            }

            // First check if user has a North Capital account ID
            if(!northCapitalAccountId) {
                setCheckingLinkedBank(false);
                return false;
            }

            // Retry logic for bank account status check
            for(let attempt = 1; attempt <= retryAttempts; attempt++) {
                try {
                    console.log(
                        `Checking bank account status - attempt ${attempt}/${retryAttempts}...`,
                    );

                    const response = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/trading/get-linked-bank-account`,
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            credentials: "include",
                            body: JSON.stringify({
                                accountId: northCapitalAccountId,
                            }),
                        },
                    );

                    // Check if API endpoint exists
                    if(!response.ok) {
                        if(response.status === 404) {
                            console.log(
                                "Bank account API endpoint not found - defaulting to new account",
                            );
                        } else {
                            console.log(
                                "Error fetching bank account info:",
                                response.status,
                            );
                        }

                        // If this is a retry attempt and we're looking for a newly linked account,
                        // wait before next attempt
                        if(attempt < retryAttempts && retryAttempts > 1) {
                            console.log(`Waiting ${delayMs}ms before retry...`);
                            await new Promise((resolve) =>
                                setTimeout(resolve, delayMs),);
                            continue;
                        }

                        setHasLinkedBank(false);
                        setPaymentOption("new");
                        return false;
                    }

                    const result = await response.json();
                    console.log(
                        `Bank account check attempt ${attempt} - API response:`,
                        result,
                    );

                    // Check for actual bank account data with proper validation
                    if(
                        result.data &&
                        result.data.bankDetails &&
                        (result.data.bankDetails.accountNumber ||
                            result.data.bankDetails.maskedAccountNumber ||
                            result.data.bankDetails.last4) &&
                        result.data.bankDetails.isActive !== false
                    ) {
                        console.log(
                            "✅ Found linked bank account:",
                            result.data.bankDetails,
                        );
                        setHasLinkedBank(true);
                        setLinkedBankInfo(result.data.bankDetails);
                        setPaymentOption("existing");

                        // Show success message if this was a retry attempt (i.e., we just linked a new account)
                        if(retryAttempts > 1 && attempt > 1) {
                            notifyUser(null, "success", "Bank account details updated!", "toast", null, null, null, null, null, false, false, "top-center");
                        }

                        return true;
                    } else {
                        console.log(
                            `No valid bank details found in attempt ${attempt}`,
                        );

                        // If this is a retry attempt, wait before next attempt
                        if(attempt < retryAttempts) {
                            console.log(`Waiting ${delayMs}ms before retry...`);
                            await new Promise((resolve) =>
                                setTimeout(resolve, delayMs),);
                            continue;
                        }

                        setHasLinkedBank(false);
                        setPaymentOption("new");
                        return false;
                    }
                } catch(fetchError) {
                    console.error(
                        `Bank account check attempt ${attempt} failed:`,
                        fetchError,
                    );

                    // If this is a retry attempt, wait before next attempt
                    if(attempt < retryAttempts) {
                        console.log(`Waiting ${delayMs}ms before retry...`);
                        await new Promise((resolve) =>
                            setTimeout(resolve, delayMs),);
                        continue;
                    }

                    throw fetchError;
                }
            }

            return false;
        } catch(error) {
            console.error("Error checking linked bank account:", error);
            // For any API errors (endpoint doesn't exist, network issues, etc.),
            // default to new account for safety
            setHasLinkedBank(false);
            setPaymentOption("new");
            console.log("Defaulting to new bank account due to API error");
            return false;
        } finally {
            if(showLoadingState) {
                setCheckingLinkedBank(false);
            }
        }
    };

    // Check for existing linked bank account on component mount
    useEffect(() => {
        // Add a small delay to ensure user data is loaded
        setTimeout(() => checkLinkedBankAccount(true), 500);
    }, [northCapitalAccountId]);

    /**
     * Auto-reload page when bank connection is successful and window is closed
     */
    useEffect(() => {
        // If bank connection was successful and iframe is closed, reload the page
        if(
            plaidConnectionSuccessful &&
            !showIframe &&
            currentStep === "ready"
        ) {
            console.log(
                "Bank linked successfully and window closed - reloading page to refresh bank details",
            );
            setTimeout(() => {
                window.location.reload();
            }, 1500);
        }
    }, [plaidConnectionSuccessful, showIframe, currentStep]);

    /**
     * Manual refresh function for bank account status
     */
    const handleRefreshBankAccount = async () => {
        console.log("Manual refresh of bank account status triggered");
        const bankFound = await checkLinkedBankAccount(true, 3, 1000);

        if(bankFound) {
            notifyUser(null, "success", "Bank account information updated!", "toast", null, null, null, null, null, false, false, "top-center");
        } else {
            notifyUser(null, "warning", "No linked bank account found. You may need to link an account first.", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    /**
     * Step 1: Create Trade with North Capital
     */
    const createTrade = async () => {
        try {
            setCurrentStep("creating-trade");
            setLoading(true);
            setError("");
            setPlaidConnectionSuccessful(false);

            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/trading/create-trade`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        offeringId,
                        accountId: northCapitalAccountId,
                        transactionType: "ACH",
                        amount,
                        campaignId,
                        acceptanceToken,
                        field1: `Campaign ${campaignId}`,
                        field2: `User ${user.id}`,
                    }),
                },
            );

            const data = await response.json();

            if(!response.ok) {
                throw new Error(
                    data.error || data.message || "Failed to create trade",
                );
            }

            if(!data.data?.tradeId) {
                throw new Error("Trade ID not received from payment processor");
            }

            setTradeId(data.data.tradeId);
            setTransactionId(data.data.transactionId);

            // Move to next step: Link external account
            await linkExternalAccount(data.data.tradeId);
        } catch(err) {
            console.error("Create trade error:", err);
            setError(
                err.message ||
                    "Failed to create investment trade. Please try again.",
            );
            setLoading(false);
            setCurrentStep("ready");
            notifyUser(null, "error", err.message || "Failed to create investment trade", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    /**
     * Step 2: Link External Account (Get Plaid URL)
     */
    const linkExternalAccount = async (tradeIdToUse) => {
        try {
            setCurrentStep("linking-account");
            setError("");

            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/trading/link-external-account`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        accountId: northCapitalAccountId,
                    }),
                },
            );

            const data = await response.json();

            if(!response.ok) {
                // Check for bank link inconsistency error
                if(data.errorCode === "BANK_LINK_INCOMPLETE") {
                    setError(data.error);
                    setLoading(false);
                    setCurrentStep("ready");
                    notifyUser(null, "error", "Bank account connection issue - please contact support or use credit card", "toast", null, null, null, null, null, false, false, "top-center");
                    return;
                }
                throw new Error(
                    data.error ||
                        data.message ||
                        "Failed to link external account",
                );
            }

            // Check if we should skip Plaid (account already linked)
            if(data.data?.skipPlaid) {
                console.log(
                    "Account already linked - skipping Plaid, proceeding to fund transfer",
                );
                // Skip Plaid iframe and go directly to external fund move
                await executeExternalFundMove(tradeIdToUse);
                return;
            }

            if(!data.data?.plaidUrl) {
                throw new Error(
                    "Plaid URL not received from payment processor",
                );
            }

            setPlaidUrl(data.data.plaidUrl);
            setShowIframe(true);
            setCurrentStep("plaid-open");
            setLoading(false);
        } catch(err) {
            console.error("Link external account error:", err);
            setError(
                err.message ||
                    "Failed to connect bank account. Please try again.",
            );
            setLoading(false);
            setCurrentStep("ready");
            notifyUser(null, "error", err.message || "Failed to connect bank account", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    /**
     * Verify external account is ready after Plaid, then execute fund move
     */
    const verifyAndExecuteFundMove = async () => {
        try {
            console.log("Verifying external account is ready...");
            setCurrentStep("verifying-account");

            // Try up to 3 times with delays to verify account is ready
            for(let attempt = 1; attempt <= 3; attempt++) {
                try {
                    console.log(`Verification attempt ${attempt}/3...`);

                    // Call a verification endpoint (we'll create this)
                    const response = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/trading/verify-external-account`,
                        {
                            method: "POST",
                            credentials: "include",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                                accountId: northCapitalAccountId,
                            }),
                        },
                    );

                    const data = await response.json();

                    if(response.ok && data.isReady) {
                        console.log("External account verified and ready!");

                        // Refresh linked bank status to show the new account with retry logic
                        await checkLinkedBankAccount(false, 3, 2000);

                        // Account is ready, proceed with fund move
                        await executeExternalFundMove();
                        return;
                    }

                    // Not ready yet, wait before next attempt
                    if(attempt < 3) {
                        console.log(
                            `Account not ready, waiting ${attempt * 2} seconds...`,
                        );
                        await new Promise((resolve) =>
                            setTimeout(resolve, attempt * 2000),);
                    }
                } catch(err) {
                    console.error(
                        `Verification attempt ${attempt} failed:`,
                        err,
                    );
                    if(attempt === 3) throw err;
                    await new Promise((resolve) =>
                        setTimeout(resolve, attempt * 2000),);
                }
            }

            // If we get here, verification failed after 3 attempts
            // But the bank connection itself was successful via Plaid
            const bankFound = await checkLinkedBankAccount(false, 4, 2000);
            if(bankFound) {
                throw new Error(
                    "Bank account linked successfully, but verification is taking longer than expected. You can try the payment again.",
                );
            } else {
                throw new Error(
                    "Bank connection completed, but account details are still processing. Please refresh page or try again in a moment.",
                );
            }
        } catch(err) {
            console.error("Verification error:", err);

            // Even if verification failed, the bank might still be linked
            // Refresh the status to show if bank is actually connected
            if(plaidConnectionSuccessful) {
                const bankFound = await checkLinkedBankAccount(false, 4, 2000);
                if(bankFound) {
                    setError(
                        "Bank account linked successfully, but payment verification failed. You can try the payment again.",
                    );
                    notifyUser(null, "warning", "Bank linked successfully - payment verification failed. You can retry.", "toast", null, null, null, null, null, false, false, "top-center");
                } else {
                    setError(
                        "Bank connection completed, but account details are still processing. Please refresh in a moment.",
                    );
                }
            } else {
                setError(
                    err.message ||
                        "Failed to verify bank account. Please contact support.",
                );
                notifyUser(null, "error", err.message || "Bank verification failed", "toast", null, null, null, null, null, false, false, "top-center");
            }

            setLoading(false);
            setShowIframe(false);
            setCurrentStep("ready");
        }
    };

    /**
     * Step 3: Execute External Fund Move
     * Called after Plaid success and verification
     * @param {string} tradeIdParam - Optional tradeId to use (for already-linked accounts)
     */
    const executeExternalFundMove = async (tradeIdParam = null) => {
        try {
            setCurrentStep("executing-payment");
            setLoading(true);
            setShowIframe(false);
            setError("");

            // Use passed tradeId or fall back to state
            const tradeIdToUse = tradeIdParam || tradeId;

            console.log("Executing external fund move with:", {
                accountId: northCapitalAccountId,
                offeringId,
                tradeId: tradeIdToUse,
                amount: parseFloat(amount),
            });

            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/trading/external-fund-move`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        accountId: northCapitalAccountId,
                        offeringId,
                        tradeId: tradeIdToUse,
                        nickName: `Investment ${tradeIdToUse}`,
                        amount: parseFloat(amount),
                        campaignId,
                        acceptanceToken,
                        description: `Investment in Campaign ${campaignId}`,
                    }),
                },
            );

            if(!response.ok) {
                const errorText = await response.text();
                console.error(
                    "External fund move failed:",
                    response.status,
                    errorText,
                );
                throw new Error(
                    `Payment failed: ${response.status} ${response.statusText}`,
                );
            }

            const data = await response.json();
            console.log("External fund move successful:", data);

            setCurrentStep("complete");
            notifyUser(null, "success", "ACH investment submitted successfully!", "toast", null, null, null, null, null, false, false, "top-center");

            // Redirect to payment success page
            setTimeout(() => {
                navigate("/payment-success", {
                    state: {
                        amount: amount,
                        totalCharge: data.data?.fees?.totalCharge,
                        fees: data.data?.fees,
                        paymentMethod: "ACH Bank Transfer",
                        transactionId: data.data?.refNum,
                        tradeId: tradeIdToUse,
                    },
                });
            }, 2000);
        } catch(err) {
            console.error("External fund move error:", err);

            // Check for ACH provider activation issue
            if(
                err.message &&
                err.message.includes("Payment Provider is not EXIST")
            ) {
                setError(
                    "ACH payment is temporarily unavailable. Please use Credit Card payment instead.",
                );
                notifyUser(null, "error", "ACH payment unavailable - please use Credit Card", "toast", null, null, 8000, null, null, false, false, "top-center");
            } else if(
                err.message.includes("Failed to fetch") ||
                err.message.includes("NetworkError")
            ) {
                setError(
                    "Network error: Unable to connect to payment processor. Please check your connection and try again.",
                );
                notifyUser(null, "error", "Network connection error - please try again", "toast", null, null, null, null, null, false, false, "top-center");
            } else if(err.message.includes("500")) {
                setError(
                    "Server error: Payment processor is temporarily unavailable. Please try again in a few minutes.",
                );
                notifyUser(null, "error", "Server temporarily unavailable", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                setError(
                    err.message ||
                        "Failed to complete payment. Please contact support.",
                );
                notifyUser(null, "error", err.message || "Failed to complete payment", "toast", null, null, null, null, null, false, false, "top-center");
            }

            setLoading(false);
            setCurrentStep("ready");
        }
    };

    /**
     * Handle Plaid iframe messages
     */
    useEffect(() => {
        const handlePlaidMessage = (event) => {
            try {
                console.log("Received message from Plaid:", event.data);

                // Parse message from Plaid iframe
                const response = JSON.parse(event.data);

                // Ignore Stripe controller messages - they're not related to Plaid
                if(
                    response.type === "parent" &&
                    response.message &&
                    response.message.action ===
                        "stripe-controller-action-response"
                ) {
                    console.debug("Ignoring Stripe controller message");
                    return;
                }

                // Ignore other non-Plaid messages
                if(
                    response.__stripeJsV3 ||
                    response.sourceFrameId?.includes(
                        "privateStripeController",
                    ) ||
                    response.controllerAppFrameId?.includes(
                        "privateStripeController",
                    )
                ) {
                    console.debug("Ignoring Stripe-related message");
                    return;
                }

                // Success: Account linked successfully
                if(
                    response.statusCode === "101" ||
                    response.statusCode === 101
                ) {
                    console.log(
                        "Plaid connection successful - waiting for payment provider to activate...",
                    );
                    setPlaidConnectionSuccessful(true);

                    // Close the iframe immediately since connection is successful
                    setShowIframe(false);

                    // Immediately try to refresh bank account status with retry logic
                    setTimeout(async () => {
                        console.log(
                            "Refreshing bank account status after Plaid success...",
                        );
                        await checkLinkedBankAccount(false, 5, 3000);

                        // Then verify the external account is ready and attempt fund move
                        await verifyAndExecuteFundMove();
                    }, 2000);
                } else if(
                    response.statusCode === "200" ||
                    response.statusCode === 200
                ) {
                    console.log("User cancelled Plaid connection");
                    setError(
                        "Bank connection was cancelled by user. You can try again anytime.",
                    );
                    setShowIframe(false);
                    setLoading(false);
                    setCurrentStep("ready");
                    notifyUser(null, "warning", "Bank connection cancelled by user", "toast", null, null, null, null, null, false, false, "top-center");
                } else if(
                    response.errorCode ||
                    response.statusCode === "300"
                ) {
                    console.error("Plaid error:", response);

                    // Before treating this as a complete failure, check if bank was actually linked
                    console.log(
                        "Checking if bank was linked despite Plaid error...",
                    );
                    setShowIframe(false);
                    setLoading(true);
                    setCurrentStep("verifying-account");

                    // Wait a moment then check for linked bank account
                    setTimeout(async () => {
                        const bankFound = await checkLinkedBankAccount(
                            false,
                            4,
                            3000,
                        );

                        if(bankFound) {
                            // Bank was actually linked successfully
                            setPlaidConnectionSuccessful(true);
                            setError("");
                            setCurrentStep("ready");
                            setLoading(false);

                            // Force refresh of the component state to show newly linked account
                            setPaymentOption("existing");

                            notifyUser(null, "success", "Bank account linked successfully! The connection details have been updated.", "toast", null, null, null, null, null, false, false, "top-center");
                        } else {
                            // Genuine connection failure
                            setError(
                                `Bank connection failed: ${response.errorMessage || response.statusDesc || "Unknown error"}`,
                            );
                            setLoading(false);
                            setCurrentStep("ready");
                            notifyUser(null, "error", "Bank connection failed. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
                        }
                    }, 2000);
                } else {
                    // Only log as unknown if it might be a legitimate Plaid message
                    if(
                        response.statusCode ||
                        response.errorCode ||
                        response.statusDesc
                    ) {
                        console.log("Unknown Plaid message:", response);
                    }
                }
            } catch{
                // Not a JSON message, might be from other sources
                console.debug("Non-JSON message received:", event.data);

                // Check if it's a cancellation message
                if(
                    typeof event.data === "string" &&
                    event.data.toLowerCase().includes("cancel")
                ) {
                    setError(
                        "Bank connection was cancelled. You can try again anytime.",
                    );
                    setShowIframe(false);
                    setLoading(false);
                    setCurrentStep("ready");
                    notifyUser(null, "warning", "Bank connection cancelled", "toast", null, null, null, null, null, false, false, "top-center");
                }
            }
        };

        window.addEventListener("message", handlePlaidMessage);

        return () => {
            window.removeEventListener("message", handlePlaidMessage);
        };
    }, [tradeId, northCapitalAccountId, offeringId, amount, campaignId]);

    /**
     * Handle iframe close button
     */
    const handleCloseIframe = () => {
        setShowIframe(false);
        setLoading(false);

        // Check if Plaid connection was successful
        if(!plaidConnectionSuccessful) {
            setError(
                "Bank connection was cancelled. Please try again when ready.",
            );
            setCurrentStep("ready");
            notifyUser(null, "warning", "Bank connection cancelled", "toast", null, null, null, null, null, false, false, "top-center");
        } else {
            // Connection was successful
            setError("");
            setCurrentStep("ready");

            // Provide different messages based on what stage we're in
            if(currentStep === "verifying-account") {
                notifyUser(null, "info", "Bank connected successfully! Account verification was interrupted - you can retry when ready.", "toast", null, null, null, null, null, false, false, "top-center");
            } else if(currentStep === "executing-payment") {
                notifyUser(null, "info", "Bank connected successfully! Payment processing was interrupted - you can retry when ready.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "success", "Bank account connected successfully!", "toast", null, null, null, null, null, false, false, "top-center");
            }
        }
    };

    /**
     * Handle direct investment with existing bank account
     */
    const handleDirectInvestment = async (e) => {
        e.preventDefault();

        // Validate user has account ID
        if(!northCapitalAccountId) {
            setError(
                "Your account is not ready for investments. Please contact support.",
            );
            notifyUser(null, "error", "Investment account not found", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // Validate amount
        const parsedAmount = parseFloat(amount);
        if(!parsedAmount || parsedAmount <= 0) {
            setError("Invalid investment amount");
            notifyUser(null, "error", "Invalid investment amount", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setLoading(true);
        setError("");

        try {
            // Create trade and immediately execute fund move for existing account
            console.log("Creating trade for existing bank account...");
            const tradeResponse = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/trading/create-trade`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        offeringId,
                        accountId: northCapitalAccountId,
                        transactionType: "ACH",
                        amount: parsedAmount,
                        campaignId,
                        acceptanceToken,
                        field1: `Campaign ${campaignId}`,
                        field2: `User ${user.id}`,
                    }),
                },
            );

            if(!tradeResponse.ok) {
                const errorText = await tradeResponse.text();
                console.error("Trade creation failed:", errorText);
                throw new Error(
                    `Failed to create trade: ${tradeResponse.status} ${tradeResponse.statusText}`,
                );
            }

            const tradeData = await tradeResponse.json();
            console.log("Trade created successfully:", tradeData);

            if(!tradeData.data?.tradeId) {
                throw new Error("Trade ID not received from payment processor");
            }

            // Execute fund move with existing bank account
            console.log(
                "Executing fund move for trade ID:",
                tradeData.data.tradeId,
            );
            await executeExternalFundMove(tradeData.data.tradeId);
        } catch(err) {
            console.error("Direct investment error:", err);
            // Provide more specific error messages
            if(err.message.includes("Failed to fetch")) {
                setError(
                    "Network error: Unable to connect to payment processor. Please check your internet connection and try again.",
                );
                notifyUser(null, "error", "Connection error - please try again", "toast", null, null, null, null, null, false, false, "top-center");
            } else if(err.message.includes("500")) {
                setError(
                    "Server error: Payment processor is temporarily unavailable. Please try again in a few minutes.",
                );
                notifyUser(null, "error", "Server temporarily unavailable", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                setError(
                    err.message ||
                        "Failed to process investment. Please try again.",
                );
                notifyUser(null, "error", err.message || "Failed to process investment", "toast", null, null, null, null, null, false, false, "top-center");
            }
            setLoading(false);
            setCurrentStep("ready");
        }
    };

    /**
     * Handle updating linked external account for existing users
     */
    const handleUpdateLinkedAccount = async (e) => {
        e.preventDefault();

        if(!northCapitalAccountId) {
            setError(
                "Your account is not ready for investments. Please contact support.",
            );
            notifyUser(null, "error", "Investment account not found", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const parsedAmount = parseFloat(amount);
        if(!parsedAmount || parsedAmount <= 0) {
            setError("Invalid investment amount");
            notifyUser(null, "error", "Invalid investment amount", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setLoading(true);
        setError("");
        setPlaidConnectionSuccessful(false);

        try {
            // First create a trade to get tradeId for updateLinkedExternalAccount
            const tradeRes = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/trading/create-trade`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                        offeringId,
                        accountId: northCapitalAccountId,
                        transactionType: "ACH",
                        amount: parsedAmount,
                        campaignId,
                        acceptanceToken,
                        field1: `Campaign ${campaignId}`,
                        field2: `User ${user.id}`,
                    }),
                },
            );

            if(!tradeRes.ok) {
                const errorText = await tradeRes.text();
                throw new Error(
                    `Failed to create trade: ${errorText.substring(0, 100)}`,
                );
            }

            const tradeResult = await tradeRes.json();

            if(!tradeResult.data || !tradeResult.data.tradeId) {
                throw new Error(
                    tradeResult.message || "Failed to create trade",
                );
            }

            // Store tradeId for later use
            const tradeId = tradeResult.data.tradeId;
            setTradeId(tradeId);

            // Call updateLinkedExternalAccount
            const updateRes = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/trading/update-linked-external-account`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                        accountId: northCapitalAccountId,
                        tradeId: tradeId,
                    }),
                },
            );

            if(!updateRes.ok) {
                const errorText = await updateRes.text();
                throw new Error(
                    `Failed to update bank account: ${errorText.substring(0, 100)}`,
                );
            }

            const updateResult = await updateRes.json();

            if(!updateResult.success && !updateResult.data) {
                throw new Error(
                    updateResult.message || "Failed to update bank account",
                );
            }

            // Show Plaid URL for bank account update
            if(updateResult.data && updateResult.data.plaidUrl) {
                setPlaidUrl(updateResult.data.plaidUrl);
                setShowIframe(true);
                setCurrentStep("plaid-open");
                setLoading(false);
                return;
            }

            throw new Error("No Plaid URL received for bank account update");
        } catch(err) {
            console.error("Update bank account error:", err.message);
            setError(err.message);
            notifyUser(null, "error", "Failed to set up new bank account: " + err.message, "toast", null, null, null, null, null, false, false, "top-center");
            setLoading(false);
            setCurrentStep("ready");
        }
    };

    /**
     * Main form submission handler (for new bank account - first-time users)
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate user has account ID
        if(!northCapitalAccountId) {
            setError(
                "Your account is not ready for investments. Please contact support.",
            );
            notifyUser(null, "error", "Investment account not found", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // Validate amount
        const parsedAmount = parseFloat(amount);
        if(!parsedAmount || parsedAmount <= 0) {
            setError("Invalid investment amount");
            notifyUser(null, "error", "Invalid investment amount", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // Start the flow
        await createTrade();
    };

    // Render different states
    const renderContent = () => {
        // Show Plaid iframe
        if(showIframe && plaidUrl) {
            return (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full relative">
                        <button
                            onClick={handleCloseIframe}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold z-10"
                            aria-label="Close"
                        >
                            ×
                        </button>
                        <div className="p-6">
                            <h3 className="text-xl font-semibold text-navy mb-4">
                                Connect Your Bank Account
                            </h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Please authenticate and select your bank account
                                through Plaid&apos;s secure connection.
                            </p>
                            <iframe
                                ref={iframeRef}
                                src={plaidUrl}
                                className="w-full border-none rounded-lg"
                                style={{ height: "500px" }}
                                title="Plaid Bank Connection"
                            />
                        </div>
                    </div>
                </div>
            );
        }

        // Processing states
        if(currentStep === "creating-trade") {
            return (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-navy mb-4"></div>
                    <p className="text-gray-600 font-medium">
                        Creating your investment trade...
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                        Please wait, do not close this window
                    </p>
                </div>
            );
        }

        if(currentStep === "linking-account") {
            return (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-navy mb-4"></div>
                    <p className="text-gray-600 font-medium">
                        Preparing secure bank connection...
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                        Opening Plaid widget
                    </p>
                </div>
            );
        }

        if(currentStep === "executing-payment") {
            return (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-navy mb-4"></div>
                    <p className="text-gray-600 font-medium">
                        Processing your payment...
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                        Completing ACH transfer
                    </p>
                </div>
            );
        }

        if(currentStep === "complete") {
            return (
                <div className="text-center py-8">
                    <div className="mb-4">
                        <svg
                            className="w-16 h-16 text-green-500 mx-auto"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        Investment Successful!
                    </h3>
                    <p className="text-gray-600">
                        Your investment is being processed. Redirecting to
                        dashboard...
                    </p>
                </div>
            );
        }

        // Show loading state while checking for linked bank accounts
        if(checkingLinkedBank) {
            return (
                <div className="space-y-4">
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-pulse text-navy text-sm flex items-center space-x-2">
                            <svg
                                className="animate-spin w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            <span>Checking bank account details...</span>
                        </div>
                    </div>
                </div>
            );
        }

        // Default: Ready to invest
        return (
            <div className="space-y-6">
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start">
                            <svg
                                className="w-5 h-5 text-red-500 mt-0.5 mr-2"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <p className="text-red-800 text-sm font-medium">
                                {error}
                            </p>
                        </div>
                    </div>
                )}

                {/* Payment Options - only show when user has a linked bank account */}
                {hasLinkedBank && linkedBankInfo && (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-[#23304a] uppercase tracking-wide">
                                Payment Method
                            </h3>
                            <button
                                type="button"
                                onClick={handleRefreshBankAccount}
                                disabled={checkingLinkedBank}
                                className="text-xs text-navy hover:text-[#003A61] font-medium flex items-center space-x-1 disabled:opacity-50"
                            >
                                <i
                                    className={`fas fa-sync-alt ${checkingLinkedBank ? "animate-spin" : ""}`}
                                ></i>
                                <span>Refresh</span>
                            </button>
                        </div>

                        {/* Option 1: Use existing bank account */}
                        <label
                            className={`flex items-center gap-3 p-3.5 border rounded-lg cursor-pointer transition-colors ${
                                paymentOption === "existing"
                                    ? "border-navy bg-[#f0f6fa]"
                                    : "border-gray-200 bg-white hover:border-navy"
                            }`}
                        >
                            <input
                                type="radio"
                                name="paymentOption"
                                value="existing"
                                checked={paymentOption === "existing"}
                                onChange={(e) =>
                                    setPaymentOption(e.target.value)
                                }
                                className="text-navy focus:ring-navy"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <i className="fas fa-university text-navy text-sm"></i>
                                    <span className="font-semibold text-[#23304a] text-sm">Linked Bank Account</span>
                                </div>
                                <div className="text-xs text-navy mt-0.5">
                                    {linkedBankInfo.bankName ||
                                        linkedBankInfo.institutionName ||
                                        "Bank"}{" "}
                                    ending in{" "}
                                    {linkedBankInfo.accountNumber ||
                                        linkedBankInfo.maskedAccountNumber ||
                                        linkedBankInfo.last4 ||
                                        linkedBankInfo.accountNumberMask ||
                                        "****"}
                                </div>
                            </div>
                        </label>

                        {/* Option 2: Update bank account - only when linked bank exists */}
                        <label
                            className={`flex items-center gap-3 p-3.5 border rounded-lg cursor-pointer transition-colors ${
                                paymentOption === "new"
                                    ? "border-navy bg-[#f0f6fa]"
                                    : "border-gray-200 bg-white hover:border-navy"
                            }`}
                        >
                            <input
                                type="radio"
                                name="paymentOption"
                                value="new"
                                checked={paymentOption === "new"}
                                onChange={(e) =>
                                    setPaymentOption(e.target.value)
                                }
                                className="text-navy focus:ring-navy"
                            />
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <i className="fa-solid fa-rotate text-navy text-sm"></i>
                                    <span className="font-semibold text-[#23304a] text-sm">Use a New Account</span>
                                </div>
                                <div className="text-xs text-amber-600 mt-0.5">
                                    Replaces your current linked account
                                </div>
                            </div>
                        </label>
                    </div>
                )}

                {/* For first-time users without linked bank, show direct add bank message */}
                {!hasLinkedBank && (
                    <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-[#23304a] uppercase tracking-wide">
                                Payment Method
                            </h3>
                            <button
                                type="button"
                                onClick={handleRefreshBankAccount}
                                disabled={checkingLinkedBank}
                                className="text-xs text-navy hover:text-[#003A61] font-medium flex items-center space-x-1 disabled:opacity-50"
                                title="Check if a bank account was recently linked"
                            >
                                <i
                                    className={`fas fa-sync-alt ${checkingLinkedBank ? "animate-spin" : ""}`}
                                ></i>
                                <span>Check Status</span>
                            </button>
                        </div>
                        <div className="p-4 border border-[#e3eaf3] rounded-xl bg-[#F5F7FA] space-y-2">
                            <div className="font-semibold text-[#23304a] text-sm">
                                Setup your bank account
                            </div>
                            <p className="text-xs text-[#6b7a8f]">
                                Connect securely through Plaid for future ACH investments.
                            </p>
                            <p className="text-xs text-[#6b7a8f]">
                                <span className="italic">Note:</span> If you already linked an account recently, click &quot;Check Status&quot; above to refresh.
                            </p>
                        </div>
                    </div>
                )}

                {/* ACH Process Info - show for new users or when updating existing account */}
                {(!hasLinkedBank || paymentOption === "new") && (
                    <div className="bg-[#f0f6fa] border border-[#d1e2f0] rounded-xl p-4">
                        <div className="flex flex-col space-y-2.5">
                            <p className="text-navy text-sm font-semibold">
                                {!hasLinkedBank
                                    ? "ACH Payment Process"
                                    : "Bank Account Update Process"}
                            </p>
                            <ul className="text-[#4A5E78] text-xs space-y-1.5 ml-4 list-disc marker:text-navy/50">
                                <li>
                                    Click &quot;
                                    {!hasLinkedBank
                                        ? "Connect & Invest"
                                        : "Update & Invest"}
                                    &quot; below
                                </li>
                                <li>
                                    {!hasLinkedBank ? "Connect" : "Update"}{" "}
                                    your bank account securely through Plaid
                                </li>
                                <li>
                                    ACH funds take 3-5 business days to settle
                                </li>
                                <li>
                                    You&apos;ll receive an email confirmation
                                </li>
                            </ul>
                        </div>
                    </div>
                )}

                {/* Investment Summary */}
                <div className="bg-[#F5F7FA] border border-[#e3eaf3] rounded-xl p-4 space-y-3">
                    <div className="flex justify-between items-center text-sm">
                        <span className="text-[#6b7a8f]">
                            Investment Amount
                        </span>
                        <span className="font-semibold text-[#23304a]">
                            {formatCurrency(amount)}
                        </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                        <span className="text-[#6b7a8f]">Fees</span>
                        <span className="font-semibold text-[#23304a]">
                            {formatCurrency(feeBreakdown?.totalFees || 0)}
                        </span>
                    </div>
                    <div className="flex justify-between items-center border-t border-[#e3eaf3] pt-3 text-sm">
                        <span className="font-semibold text-[#23304a]">
                            Total Charge
                        </span>
                        <span className="font-bold text-navy">
                            {formatCurrency(feeBreakdown?.totalCharge || amount)}
                        </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                        <span className="text-[#6b7a8f]">Payment Method</span>
                        <span className="font-semibold text-[#23304a]">
                            {!hasLinkedBank
                                ? "Bank Account (New)"
                                : paymentOption === "existing"
                                  ? "Linked Bank Account"
                                  : "Update Bank Account"}
                        </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                        <span className="text-[#6b7a8f]">Settlement Time</span>
                        <span className="font-medium text-[#23304a]">
                            3-5 Business Days
                        </span>
                    </div>
                </div>

                {loading && (
                    <div className="flex justify-center items-center my-4">
                        <div className="animate-pulse text-navy text-sm flex items-center space-x-2">
                            <svg
                                className="animate-spin w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            <span>Processing your investment...</span>
                        </div>
                    </div>
                )}

                {/* Action Button based on payment option */}
                {hasLinkedBank && paymentOption === "existing" ? (
                    <button
                        type="button"
                        onClick={handleDirectInvestment}
                        disabled={loading}
                        className="w-full px-5 py-3 bg-navy text-white rounded-lg hover:bg-navy-2 font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
                    >
                        {loading ? (
                            <>
                                <i className="fas fa-circle-notch fa-spin"></i>
                                <span>Processing...</span>
                            </>
                        ) : (
                            <>
                                <span>Pay {formatCurrency(feeBreakdown?.totalCharge || amount)} with Linked Bank</span>
                                <i className="fas fa-arrow-right transform transition-transform group-hover:translate-x-1"></i>
                            </>
                        )}
                    </button>
                ) : hasLinkedBank && paymentOption === "new" ? (
                    <button
                        type="button"
                        onClick={handleUpdateLinkedAccount}
                        disabled={loading}
                        className="w-full px-5 py-3 bg-navy text-white rounded-lg hover:bg-navy-2 font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
                    >
                        {loading ? (
                            <>
                                <i className="fas fa-circle-notch fa-spin"></i>
                                <span>Processing...</span>
                            </>
                        ) : (
                            <>
                                <span>Update Bank & Pay {formatCurrency(feeBreakdown?.totalCharge || amount)}</span>
                                <i className="fas fa-arrow-right transform transition-transform group-hover:translate-x-1"></i>
                            </>
                        )}
                    </button>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full px-5 py-3 bg-navy text-white rounded-lg hover:bg-navy-2 font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:bg-[#a1a1aa] disabled:cursor-not-allowed flex items-center justify-center gap-2 group"
                        >
                            {loading ? (
                                <>
                                    <i className="fas fa-circle-notch fa-spin"></i>
                                    <span>Processing...</span>
                                </>
                            ) : (
                                <>
                                    <span>Connect & Pay {formatCurrency(feeBreakdown?.totalCharge || amount)}</span>
                                    <i className="fas fa-arrow-right transform transition-transform group-hover:translate-x-1"></i>
                                </>
                            )}
                        </button>
                    </form>
                )}

                <p className="text-xs text-[#6b7a8f] text-center px-4">
                    By confirming, you agree to proceed with this investment
                    transaction. Funds will be transferred securely via ACH.
                </p>
            </div>
        );
    };

    return <div className="relative">{renderContent()}</div>;
}

NorthCapitalAchForm.propTypes = {
    offeringId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    acceptanceToken: PropTypes.string.isRequired,
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        // Add other user fields if needed
    }).isRequired,
    northCapitalAccountId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
};

export default NorthCapitalAchForm;
